import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// Idle-session activity tracking, shared by AuthContext (DOM listeners) and
// the response interceptor below (any successful API call counts as activity,
// which is how driver GPS pings count without touching DriverPortal).
const ACTIVITY_KEY = "nemt_last_activity";
const ACTIVITY_THROTTLE_MS = 5000;
let lastActivity = Number(localStorage.getItem(ACTIVITY_KEY)) || Date.now();

export function getLastActivity() {
  return lastActivity;
}

// Throttled to at most once per 5s so scroll/mousemove don't hammer localStorage.
// Pass force=true to bypass the throttle (e.g. right after login).
export function markActivity(force) {
  const now = Date.now();
  if (!force && now - lastActivity < ACTIVITY_THROTTLE_MS) return;
  lastActivity = now;
  localStorage.setItem(ACTIVITY_KEY, String(now));
}

const api = axios.create({ baseURL: API_BASE });

api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("nemt_token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

api.interceptors.response.use(
  (r) => {
    markActivity();
    return r;
  },
  (err) => {
    if (err.response?.status === 401 && !window.location.pathname.startsWith("/login")) {
      localStorage.removeItem("nemt_token");
      localStorage.removeItem("nemt_user");
      window.location.href = "/login";
    } else if (err.response?.status === 403 && err.response?.data?.detail === "mfa_setup_required") {
      let role = null;
      try {
        role = JSON.parse(localStorage.getItem("nemt_user") || "null")?.role;
      } catch {
        role = null;
      }
      const target = role === "driver" ? "/driver/account" : "/app/account";
      if (!window.location.pathname.startsWith(target)) {
        window.location.href = `${target}?mfa=required`;
      }
    } else if (err.response?.status === 402 && err.response?.data?.detail === "subscription_required") {
      let role = null;
      try {
        role = JSON.parse(localStorage.getItem("nemt_user") || "null")?.role;
      } catch {
        role = null;
      }
      const target = role === "driver" ? "/driver/subscription-ended" : "/app/subscription";
      if (!window.location.pathname.startsWith(target)) {
        window.location.href = target;
      }
    }
    return Promise.reject(err);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
